import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EmployeeListService {

  employeeListApi = environment.apiToGetAllEmployeeList;
    // Cache for final data
    private cachedUserList: any[] | null = null;

    // Cache for the observable so multiple subscribers share the same request
    private userList$!: Observable<any[]>;
    private bkUserList$!: Observable<any[]>;

  constructor(private http: HttpClient) { }

  getEmployeeList():Observable<any>{
    return this.http.get(this.employeeListApi);
  }

  //Service Principal list API
  getServicePrincipalList(): Observable<any> {
    return this.http.get<any>(environment.apiServicePrinciple);
  }

  apiBatch(body: any): Observable<any> {
    return this.http.post<any>(environment.apiBatch, body);
  }

  // The combined method that calls both endpoints in sequence
  getUserList(): Observable<any> {
    // If we already have the final data in memory, return it as an Observable:
    if (this.cachedUserList) {
      return of(this.cachedUserList);
    }

    // If we don't yet have an observable in flight, create one:
    if (!this.userList$) {
      this.userList$ = this.getServicePrincipalList().pipe(
        switchMap((data: any) => {
          // 1) First API call returns servicePrinciple array
          const servicePrinciple = data.value;

          // 2) Prepare the batch request
          const result = {
            requests: servicePrinciple.map((item: any) => ({
              id: item.id,
              method: 'GET',
              url: `/users/${item.principalId}?$select=displayName,userPrincipalName,givenName,surname`,
              headers: { Accept: 'application/json' }
            }))
          };

          // 3) Call the second API (batch)
          return this.apiBatch(result);
        }),
        // 4) Transform the batch response
        map((batchResponse: any) => {
          const filterData = batchResponse.responses.map((item: any) => ({
            fullName: item.body.displayName,
            email: item.body.userPrincipalName,
            prename: item.body.givenName,
            surname: item.body.surname
          }));
          return filterData;
        }),
        switchMap((users: any[]) => {
          // Create an array of Observables, each fetching one user’s picture
          const userWithPicRequests = users.map((user) =>
            this.getProfilePicture(user.email).pipe(
              map((blob: Blob) => {
                // Convert the Blob into a local URL
                const blobUrl = URL.createObjectURL(blob);
                return { ...user, profilePicUrl: blobUrl };
              }),
              catchError((err) => {
                console.error(`Error fetching picture for ${user.email}`, err);
                // Fallback for the photo
                return of({ ...user, profilePicUrl: 'assets/images/no_profile.png' });
              })
            )
          );

          // forkJoin waits for all Observables in the array to complete,
          // then emits an array of results
          return forkJoin(userWithPicRequests);
        }),
        // 5) Cache the final result in memory
        tap((finalList) => {
          this.cachedUserList = finalList;
        }),
        // 6) shareReplay(1) ensures the same result is shared among subscribers
        shareReplay(1)
      );
    }

    // Return the ongoing/pending Observable so subsequent subscribers share it
    return this.userList$;
  }

  getProfilePicture(emailId: string): Observable<any> {
    return this.http.get<any>(`https://graph.microsoft.com/v1.0/users/`+ emailId +`/photo/$value`, { responseType: 'blob' as 'json' });
  }

  getUserProfilePicture(): Observable<{ email: string; profilePicUrl: string }[]> {
    return this.getUserList().pipe(
      map(users => users.map((user: any) => ({
        email: user.email,
        profilePicUrl: user.profilePicUrl
      })))
    );
  }

  getUserById(id: string): Observable<any> {
    const url = `https://graph.microsoft.com/v1.0/users/${id}`;
    return this.http.get<any>(url);
  }

  getAllBkUsers(): Observable<any> {
    return this.getServicePrincipalList().pipe(
    map(response => {
      const assignments = response?.value ?? [];
      return assignments.filter((item: any) => item.appRoleId === environment.bookeeperRoleId);
    }),
    switchMap((filteredAssignments: any[]) => {
      const userDetailsRequests = filteredAssignments.map(assign =>
        this.getUserById(assign.principalId)
      );
      return forkJoin(userDetailsRequests);
    }),
    map((users: any[]) => {
      return users.map(user => user.mail ?? user.userPrincipalName);
    })
  );
  }

  // getDrive(): Observable<any> {
  //   return this.http.get<any>(`https://graph.microsoft.com/v1.0/users/automate@bosenet.com/drive`);
  // }
}
